import { Experiment, Organization} from '@/api/';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { useStore } from '@nanostores/react';
import { Badge, Loader2, Play, CheckCircle, XCircle } from 'lucide-react';
import { DeleteExperimentConfirmation } from './DeleteExperimentConfirmation';
import { cn } from '@/lib/utils';
/**
 * We'll update the selected project in the store and route
 * to the project page.
 */
function handleSelectExperiment(
  organization: Organization,
  experiment: Experiment,
) {
  $experiment.set(experiment);

  openPage($router, 'experiment', {
    organizationId: organization.id,
    experimentId: experiment.id,
  });
}

export const ExperimentListing = () => {
  const org = useStore($org);

  const experiments = useStore($experiments);
  const loadingExperiments = useStore($loadingExperiments);

  const sortedExperiments = experiments
    ?.slice()
    ?.sort(
      (a, b) =>
        new Date(b.createdAtUtc).getTime() - new Date(a.createdAtUtc).getTime(),
    );

  const [isDeleteExperimentModalOpen, setIsDeleteExperimentModalOpen] =
    useState(false);
  const [experimentToBeDeleted, setExperimentToBeDeleted] = useState('');

  const selectedExperiment = useStore($experiment);
  
  // Handle empty state when no account receivables exist
  if (!loadingExperiments && (!experiments || experiments.length === 0)) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p>No experiments found.</p>
      </div>
    );
  }

  const formatDateString = (date: string | Date) => {
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0'); // Months are zero-based, so add 1
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}-${day}-${year}`;
  };


  function getDateRangeString(
    startDate: string | Date,
    endDate: string | Date,
  ) {
    const startDateString = formatDateString(startDate);

    const endDateString = formatDateString(endDate);

    if (startDateString === endDateString) {
      return `for ${startDateString}`;
    } else {
      return `between ${startDateString} and ${endDateString}`;
    }
  }


  return (
    <>
      {loadingExperiments ? (
        <div className="flex h-32 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <ul role="list" className="divide-y divide-border">
          {sortedExperiments?.map(experiment => {
            return (
              <li
                key={experiment.id}
                className="flex items-center justify-between gap-x-6 py-5"
              >

                <div className="min-w-0">
                  <div className="flex items-center gap-x-3">
                    <p className="text-sm font-semibold leading-6">
                      {experiment.name}
                    </p>
                    <div className="mt-1 flex items-center gap-x-2 text-xs leading-5 text-muted-foreground">
                    <p className="whitespace-nowrap">
                      Start date{' '}
                      <time dateTime={experiment.startDate}>
                        {new Date(experiment.startDate).toLocaleDateString()}
                      </time>
                    </p>
                    <svg viewBox="0 0 2 2" className="h-0.5 w-0.5 fill-current">
                      <circle cx={1} cy={1} r={1} />
                    </svg>
                    <p className="truncate">
                      End date{' '}
                      <time dateTime={experiment.endDate ?? ''}>
                        {experiment.endDate ? new Date(experiment.endDate).toLocaleDateString() : 'No end date'}
                      </time>
                    </p>
                  </div>


                  </div>

                  <div className="mt-1 flex items-center gap-x-2 text-xs leading-5 text-muted-foreground">
                    <p className="whitespace-nowrap">
                      Created on{' '}
                      <time dateTime={experiment.createdAtUtc}>
                        {new Date(experiment.createdAtUtc).toLocaleDateString()}
                      </time>
                    </p>
                    <svg viewBox="0 0 2 2" className="h-0.5 w-0.5 fill-current">
                      <circle cx={1} cy={1} r={1} />
                    </svg>
                    <p className="truncate">
                      Created by {experiment.createdBy?.email || 'Unknown'}
                    </p>
                  </div>

                  <div className="mt-1 flex items-center gap-x-2 text-sm leading-5 text-muted-foreground">


                    <div className="flex items-start">
                      <div>
                        <div className="flex items-center justify-start">
                          <svg
                            viewBox="0 0 2 2"
                            className="h-0.5 w-0.5 fill-current"
                          >
                            <circle cx={1} cy={1} r={1} />
                          </svg>
                          <p className="ml-2 truncate">
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-none items-center gap-x-4">
                  <>
                    <Button
                      onClick={e => {
                        if (org ) handleSelectExperiment(org,experiment);
                      }}
                    >
                      View Experiment
                      <span className="sr-only">, {experiment.name}</span>
                    </Button>
                  </>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon">
                        <EllipsisVerticalIcon className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56">
                      <DropdownMenuLabel>Options</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => {
                          setExperimentToBeDeleted(experiment.id);
                          setIsDeleteExperimentModalOpen(true);
                        }}
                      >
                        Delete Experiment
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <DeleteExperimentConfirmation
        isOpen={isDeleteExperimentModalOpen}
        handleOpen={setIsDeleteExperimentModalOpen}
        experimentId={experimentToBeDeleted}
        organizationId={org?.id}
      />
    </>
  );
};
